import React from 'react';
import styled from 'styled-components';
import { MessageSquare } from 'lucide-react';
import { LLMProvider } from '../../types';
import { useChatStore } from '../../stores';

const SidebarContainer = styled.aside`
  width: 280px;
  background: #f9fafb;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ConversationList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
`;

const ConversationItem = styled.div<{ active?: boolean }>`
  padding: 1rem;
  margin-bottom: 0.5rem;
  border-radius: 1rem;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  background: ${props => props.active ? '#f0f9ff' : 'transparent'};
  border: 1px solid ${props => props.active ? '#0284c7' : 'transparent'};
  
  &:hover {
    background: ${props => props.active ? '#f0f9ff' : '#f3f4f6'};
    border-color: ${props => props.active ? '#0284c7' : '#e5e7eb'};
  }
`;

const ConversationTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ConversationMeta = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LLMTag = styled.span<{ provider: LLMProvider }>`
  padding: 2px 0.25rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${props => {
    switch (props.provider) {
      case 'yandex':
        return '#FF6B35';
      case 'gigachat':
        return '#00A86B';
      case 'perplexity':
        return '#6366F1';
      default:
        return '#f9fafb';
    }
  }};
  color: white;
`;

const SidebarFooter = styled.div`
  padding: 1rem;
  border-top: 1px solid #e5e7eb;
`;

const EmptyState = styled.div`
  padding: 2rem 1rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
`;

const EmptyIcon = styled.div`
  margin-bottom: 1rem;
  color: #9ca3af;
`;

// Функция для форматирования времени
const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Только что';
  if (diffMins < 60) return `${diffMins} мин назад`;
  if (diffHours < 24) return `${diffHours} ч назад`;
  if (diffDays === 1) return 'Вчера';
  if (diffDays < 7) return `${diffDays} дней назад`;
  return date.toLocaleDateString('ru-RU');
};

const SimpleSidebar: React.FC = () => {
  const { 
    conversations, 
    activeConversationId, 
    setActiveConversation
  } = useChatStore();

  const handleConversationSelect = (conversationId: string) => {
    setActiveConversation(conversationId);
  };

  // Сортируем диалоги по дате обновления (новые сверху)
  const sortedConversations = [...conversations].sort((a, b) => 
    new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime()
  );

  return (
    <SidebarContainer>
      <ConversationList>
        {sortedConversations.length === 0 ? (
          <EmptyState>
            <EmptyIcon>
              <MessageSquare size={48} />
            </EmptyIcon>
            <div>Нет диалогов</div>
            <div style={{ marginTop: '0.5rem', fontSize: '0.75rem' }}>
              Начните новый чат!
            </div>
          </EmptyState>
        ) : (
          sortedConversations.map((conversation) => (
            <ConversationItem
              key={conversation.id}
              active={activeConversationId === conversation.id}
              onClick={() => handleConversationSelect(conversation.id)}
            >
              <ConversationTitle>
                {conversation.title || 'Новый диалог'}
              </ConversationTitle>
              <ConversationMeta>
                <LLMTag provider={conversation.llmProvider}>
                  {conversation.llmProvider.toUpperCase()}
                </LLMTag>
                <span>{formatTimeAgo(conversation.updatedAt || conversation.createdAt)}</span>
              </ConversationMeta>
            </ConversationItem>
          ))
        )}
      </ConversationList>
      
      <SidebarFooter>
        {/* Пустой футер - информация о пользователе перенесена в хедер */}
      </SidebarFooter>
    </SidebarContainer>
  );
};

export default SimpleSidebar;
